import { studentsFromAhqExport, isAhqPermissionExport } from "./ahqPermission";
import {
  findStudentByNameAndBirthday,
  GRADE_OPTIONS,
  grades,
  parseBirthday,
  schools,
  type School,
  type Student,
} from "./data";
import { parseCsv } from "./csv";
import { consentFlags, type PermissionAnswers } from "./permissionForm";

export type StudentInput = {
  given: string;
  family: string;
  email: string;
  school: School;
  grade: number;
  birthday: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  emergencyName: string;
  emergencyPhone: string;
};

export const STUDENT_IMPORT_HEADERS = [
  "First name",
  "Last name",
  "Birthday",
  "Email",
  "School district",
  "Grade",
  "Guardian",
  "Guardian email",
  "Guardian phone",
  "Emergency",
  "Emergency phone",
  "Permission slips",
  "Medical",
  "Conduct",
] as const;

export type ImportedConsent = {
  permissionSlips: boolean;
  medical: boolean;
  conduct: boolean;
};

export type ParsedStudentRow = {
  input: StudentInput;
  consents: ImportedConsent;
  /** Official permission form, when the file is the AfterSchoolHQ export. */
  permission?: PermissionAnswers;
};

export type ImportRowError = {
  line: number;
  message: string;
};

export type NewImportRow = {
  key: string;
  line: number;
  parsed: ParsedStudentRow;
};

export type DuplicateImportRow = {
  key: string;
  line: number;
  parsed: ParsedStudentRow;
  existing: Student;
};

export type StudentImportPreview = {
  headers: string[];
  news: NewImportRow[];
  duplicates: DuplicateImportRow[];
  errors: ImportRowError[];
};

const HEADER_ALIASES: Record<string, string> = {
  "first name": "given",
  first: "given",
  given: "given",
  "last name": "family",
  last: "family",
  family: "family",
  birthday: "birthday",
  birthdate: "birthday",
  dob: "birthday",
  "date of birth": "birthday",
  email: "email",
  "student email": "email",
  school: "school",
  "school district": "school",
  district: "school",
  grade: "grade",
  guardian: "guardianName",
  "guardian name": "guardianName",
  "guardian email": "guardianEmail",
  "guardian phone": "guardianPhone",
  emergency: "emergencyName",
  "emergency name": "emergencyName",
  "emergency phone": "emergencyPhone",
  "permission slips": "permissionSlips",
  permission: "permissionSlips",
  medical: "medical",
  conduct: "conduct",
};

function headerKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function mapHeaders(headers: string[]): Record<string, number> {
  const map: Record<string, number> = {};
  headers.forEach((header, index) => {
    const field = HEADER_ALIASES[headerKey(header)];
    if (field && map[field] === undefined) map[field] = index;
  });
  return map;
}

function cell(
  row: string[],
  columns: Record<string, number>,
  field: string,
): string {
  const index = columns[field];
  if (index === undefined) return "";
  return (row[index] ?? "").trim();
}

function parseSchool(value: string): School | null {
  const key = value.trim().toLowerCase();
  return schools.find((school) => school.toLowerCase() === key) ?? null;
}

function parseGrade(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const byLabel = GRADE_OPTIONS.find(
    (item) => item.label.toLowerCase() === trimmed.toLowerCase(),
  );
  if (byLabel) return byLabel.value;
  if (/^-?\d+$/.test(trimmed)) {
    const grade = Number(trimmed);
    return grades.includes(grade) ? grade : null;
  }
  const match = trimmed.match(/-?\d+/);
  if (!match) return null;
  const grade = Number(match[0]);
  return grades.includes(grade) ? grade : null;
}

function parseYes(value: string, fallback = true): boolean {
  if (!value.trim()) return fallback;
  return /^(yes|y|true|1|signed)$/i.test(value.trim());
}

export function previewStudentImport(
  csvText: string,
  students: Student[],
): StudentImportPreview {
  const table = parseCsv(csvText);
  if (isAhqPermissionExport(table)) return previewAhqImport(table, students);
  const headers = table[0] ?? [];
  const columns = mapHeaders(headers);
  const errors: ImportRowError[] = [];
  const news: NewImportRow[] = [];
  const duplicates: DuplicateImportRow[] = [];
  const seen = new Map<string, number>();

  if (table.length < 2) {
    return {
      headers,
      news,
      duplicates,
      errors: [{ line: 1, message: "Add a header row and at least one student." }],
    };
  }

  const required = ["given", "family", "birthday"] as const;
  const missing = required.filter((field) => columns[field] === undefined);
  if (missing.length > 0) {
    return {
      headers,
      news,
      duplicates,
      errors: [
        {
          line: 1,
          message:
            "Need columns for first name, last name, and birthday so we can match students already in Lift.",
        },
      ],
    };
  }

  table.slice(1).forEach((row, index) => {
    const line = index + 2;
    const given = cell(row, columns, "given");
    const family = cell(row, columns, "family");
    const birthday = parseBirthday(cell(row, columns, "birthday"));
    const email = cell(row, columns, "email");
    const school = parseSchool(cell(row, columns, "school"));
    const grade = parseGrade(cell(row, columns, "grade"));
    const guardianName = cell(row, columns, "guardianName");
    const guardianEmail = cell(row, columns, "guardianEmail");
    const guardianPhone = cell(row, columns, "guardianPhone");
    const emergencyName = cell(row, columns, "emergencyName") || guardianName;
    const emergencyPhone = cell(row, columns, "emergencyPhone") || guardianPhone;

    if (!given || !family) {
      errors.push({ line, message: "First and last name are required." });
      return;
    }
    if (!birthday) {
      errors.push({
        line,
        message: "Birthday is required (YYYY-MM-DD or M/D/YYYY).",
      });
      return;
    }
    if (email && !email.includes("@")) {
      errors.push({ line, message: "Student email must be valid when provided." });
      return;
    }
    if (!school) {
      errors.push({
        line,
        message: `School must be ${schools.join(", ")}.`,
      });
      return;
    }
    if (grade === null || grade < 6 || grade > 12) {
      errors.push({ line, message: "Grade must be 6 through 12." });
      return;
    }
    if (!guardianName || !guardianEmail || !guardianPhone) {
      errors.push({ line, message: "Guardian name, email, and phone are required." });
      return;
    }

    const key = `${given.toLowerCase()}|${family.toLowerCase()}|${birthday}`;
    if (seen.has(key)) {
      errors.push({
        line,
        message: `Same name and birthday as row ${seen.get(key)}.`,
      });
      return;
    }
    seen.set(key, line);

    const parsed: ParsedStudentRow = {
      input: {
        given,
        family,
        email,
        school,
        grade,
        birthday,
        guardianName,
        guardianEmail,
        guardianPhone,
        emergencyName,
        emergencyPhone,
      },
      consents: {
        permissionSlips: parseYes(cell(row, columns, "permissionSlips")),
        medical: parseYes(cell(row, columns, "medical")),
        conduct: parseYes(cell(row, columns, "conduct")),
      },
    };

    const existing = findStudentByNameAndBirthday(
      students,
      given,
      family,
      birthday,
    );
    if (existing) {
      duplicates.push({ key: `${line}-${existing.id}`, line, parsed, existing });
    } else {
      news.push({ key: `${line}-new`, line, parsed });
    }
  });

  return { headers, news, duplicates, errors };
}

function previewAhqImport(
  table: string[][],
  students: Student[],
): StudentImportPreview {
  const headers = table[1] ?? [];
  const errors: ImportRowError[] = [];
  const news: NewImportRow[] = [];
  const duplicates: DuplicateImportRow[] = [];
  const seen = new Map<string, number>();

  studentsFromAhqExport(table).forEach((filed) => {
    const flags = consentFlags(filed.permission);
    const school = filed.school || null;
    const grade = typeof filed.grade === "number" ? filed.grade : null;
    const guardianName = filed.guardianName;
    const emergencyName = filed.emergencyName || guardianName;
    const emergencyPhone = filed.emergencyPhone || filed.guardianPhone;
    const line = filed.line;

    if (!filed.given || !filed.family) {
      errors.push({ line, message: "First and last name are required." });
      return;
    }
    if (!filed.birthday) {
      errors.push({
        line,
        message: "Birthday is required (YYYY-MM-DD or M/D/YYYY).",
      });
      return;
    }
    if (filed.email && !filed.email.includes("@")) {
      errors.push({ line, message: "Student email must be valid when provided." });
      return;
    }
    if (!school) {
      errors.push({
        line,
        message: `School must be ${schools.join(", ")}.`,
      });
      return;
    }
    if (grade === null || grade < 6 || grade > 12) {
      errors.push({ line, message: "Grade must be 6 through 12." });
      return;
    }
    if (!guardianName || !filed.guardianEmail || !filed.guardianPhone) {
      errors.push({ line, message: "Guardian name, email, and phone are required." });
      return;
    }

    const key = `${filed.given.toLowerCase()}|${filed.family.toLowerCase()}|${filed.birthday}`;
    if (seen.has(key)) {
      errors.push({
        line,
        message: `Same name and birthday as row ${seen.get(key)}.`,
      });
      return;
    }
    seen.set(key, line);

    const parsed: ParsedStudentRow = {
      input: {
        given: filed.given,
        family: filed.family,
        email: filed.email,
        school,
        grade,
        birthday: filed.birthday,
        guardianName,
        guardianEmail: filed.guardianEmail,
        guardianPhone: filed.guardianPhone,
        emergencyName,
        emergencyPhone,
      },
      consents: {
        permissionSlips: flags.permissionSlips,
        medical: flags.medical,
        conduct: flags.conduct,
      },
      permission: filed.permission,
    };
    const existing = findStudentByNameAndBirthday(
      students,
      filed.given,
      filed.family,
      filed.birthday,
    );
    if (existing) {
      duplicates.push({ key: `${line}-${existing.id}`, line, parsed, existing });
    } else {
      news.push({ key: `${line}-new`, line, parsed });
    }
  });

  return { headers, news, duplicates, errors };
}

export function studentImportTemplateCsv(): string {
  const example = [
    "Jordan",
    "Lee",
    "2013-04-02",
    "jordan.lee@example.test",
    "Northport",
    "8",
    "Sam Lee",
    "sam.lee@example.test",
    "(231) 555-0199",
    "Sam Lee",
    "(231) 555-0199",
    "yes",
    "yes",
    "yes",
  ];
  return [STUDENT_IMPORT_HEADERS.join(","), example.join(",")].join("\n");
}
